'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useWallets } from '@morfi/backend-sdk-v2/client';
import { deriveOutcomeMintPda } from '@morfi/contract-sdk';
import { PublicKey } from '@solana/web3.js';
import BN from 'bn.js';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { GiftHowItWorksSheet } from '@/components/gift/gift-how-it-works-sheet';
import { GiftSuccessModal } from '@/components/gift/gift-success-modal';
import { SendGiftStep1 } from '@/components/gift/send-gift-step1';
import { SendGiftStep2 } from '@/components/gift/send-gift-step2';
import { SendGiftStep3 } from '@/components/gift/send-gift-step3';
import { SendGiftStep4 } from '@/components/gift/send-gift-step4';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { InformationCircleIcon } from '@/components/ui/icon/glyphs';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { computeGiftFeeD6, computeMaxGiftAmountD6, computeTotalDeductedD6, GIFT_MESSAGE_MAX_BYTES, MIN_GIFT_AMOUNT_USDC_D6 } from '@/config/gift';
import { D6_SCALE } from '@/config/solana';
import { onEvent } from '@/events';
import { useGiftFeeBps } from '@/hooks/gifts/use-gift-fee';
import { type GiftQuoteAsset, useGiftQuote } from '@/hooks/gifts/use-gift-quote';
import { useHandleCheck } from '@/hooks/gifts/use-handle-check';
import { useSendUsdcGift } from '@/hooks/gifts/use-send-gift';
import { useSendOutcomeTokenGift } from '@/hooks/gifts/use-send-outcome-token-gift';
import { useXHandleCheck } from '@/hooks/gifts/use-x-handle-check';
import { useFormattedCurrency, useQuickAmounts } from '@/lib/currency';
import { estimateOutcomeTokenSpotValueFromStringsD6, parseOutcomeTokenDisplayAmountD6 } from '@/lib/gifts/outcome-token-spot-value';
import { networkProgramId } from '@/lib/solana/program-id';
import { type DebugLogger, makeDebugLogger } from '@/lib/telemetry/logger';
import { cn } from '@/lib/ui/utils';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { useWalletBalances } from '@/queries/wallet-balance';
import { type GiftFormState, getStep1Errors, getStep2Errors, resolveGiftSenderWallet, useGiftFormStore } from '@/stores/gift-form';
export interface SendGiftModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialPlatform?: 'morfi' | 'x';
  initialHandle?: string;
}
const TOTAL_STEPS = 4;
const giftLog: DebugLogger = makeDebugLogger('gifts');
function utf8Bytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** Percentage tiers offered as quick-amount presets for an outcome-token gift. */
const TOKEN_PRESET_FRACTIONS: readonly number[] = [0.25, 0.5, 0.75];

/**
 * Build the 25/50/75 % token-quantity presets for the outcome-token amount
 * field. Each `value` is a token count (display units) floored to 2 dp so it
 * can never round above the held balance; tiers that floor to 0 are dropped
 * (tiny holdings just show the Max button). 100 % is the separate Max button.
 */
function buildTokenPercentPresets(balanceDisplay: number): readonly {
  label: string;
  value: number;
}[] {
  return TOKEN_PRESET_FRACTIONS.flatMap(fraction => {
    const value = Math.floor(balanceDisplay * fraction * 100) / 100;
    if (value <= 0) return [];
    return [{
      label: `${String(Math.round(fraction * 100))}%`,
      value
    }];
  });
}
interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
}
function StepIndicator(t0) {
  const $ = _c(5);
  const {
    currentStep,
    totalSteps
  } = t0;
  let t1;
  if ($[0] !== currentStep || $[1] !== totalSteps) {
    let t2;
    if ($[3] !== currentStep) {
      t2 = (_, i) => <div key={`step-${String(i)}`} className={cn("h-1.5 flex-1 rounded-full transition-colors", i < currentStep ? "bg-foreground-primary" : "bg-background-tertiary")} />;
      $[3] = currentStep;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = <div className="flex gap-1">{Array.from({
        length: totalSteps
      }, t2)}</div>;
    $[0] = currentStep;
    $[1] = totalSteps;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
interface SendGiftModalController {
  formStep: 1 | 2 | 3 | 4;
  asset: 'usdc' | 'outcome_token';
  platform: 'morfi' | 'x';
  step: ReturnType<typeof useSendUsdcGift>['step'];
  sendError: string | null;
  errorKey: string | null;
  phase: 'compose' | 'success';
  result: ReturnType<typeof useSendUsdcGift>['result'];
  morfi: ReturnType<typeof useHandleCheck>;
  x: ReturnType<typeof useXHandleCheck>;
  balanceUsdc: number;
  balanceD6Bigint: bigint;
  senderAddress: string;
  usdcQuickAmounts: readonly {
    label: string;
    value: number;
  }[];
  tokenQuickAmounts: readonly {
    label: string;
    value: number;
  }[];
  displayAmountD6: BN;
  displayFeeD6: BN;
  willInitNextCycle: boolean;
  quoteLoading: boolean;
  nextDisabled: boolean;
  submitDisabled: boolean;
  recipientDisplay: string;
  recipientHandleNormalized: string;
  successAmountD6: string;
  permalinkUrl: string;
  outcomeSendMint: string | null;
  outcomeSendAmountD6: string;
  outcomeSendLabel: string | null;
  outcomeSendMarketTitle: string | null;
  handleNext: () => void;
  handleBack: () => void;
  handleSkipMessage: () => void;
  handleSubmit: () => Promise<void>;
  handleOpenChange: (next: boolean) => void;
  onSendAnother: () => void;
}

/**
 * Orchestration for the send-gift flow — live wallet balance, the
 * server-authoritative cash quote, exact outcome-token amounts and spot
 * estimates, Next/Back/Submit navigation, the on-chain submission via the
 * gift hooks, and the success hand-off. Kept as a hook in this file (not a
 * separate module) so the two intentional controlled-dialog effects keep
 * their file-keyed verified-false-positive exemptions (exhaustive-deps /
 * no-event-handler / set-state-in-effect).
 */
function useSendGiftModal(props) {
  const $ = _c(91);
  const {
    toUsdc,
    exchangeRate
  } = useFormattedCurrency();
  const {
    activeWallet
  } = useActiveWallet();
  const {
    wallets
  } = useWallets();
  let t0;
  if ($[0] !== wallets) {
    t0 = wallets.filter(_temp);
    $[0] = wallets;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const availableWallets = t0;
  const {
    enabled: multiWalletEnabled,
    primaryWallet,
    canStartOperation
  } = useMultiWalletFeature();
  const {
    feeBps
  } = useGiftFeeBps();
  const usdcGift = useSendUsdcGift();
  const outcomeGift = useSendOutcomeTokenGift();
  const formStep = useGiftFormStore(_temp2);
  const platform = useGiftFormStore(_temp3);
  const handle = useGiftFormStore(_temp4);
  const senderWalletId = useGiftFormStore(_temp5);
  const asset = useGiftFormStore(_temp6);
  const amount = useGiftFormStore(_temp7);
  const selectedHolding = useGiftFormStore(_temp8);
  const message = useGiftFormStore(_temp9);
  const anonymous = useGiftFormStore(_temp10);
  const maxOverrideD6Str = useGiftFormStore(_temp11);
  const setStep = useGiftFormStore(_temp12);
  const reset = useGiftFormStore(_temp13);
  const markStepAttempted = useGiftFormStore(_temp14);
  const [phase, setPhase] = useState("compose");
  const initialAppliedRef = useRef(false);
  let t1;
  if ($[2] !== activeWallet?.id || $[3] !== availableWallets || $[4] !== props.initialHandle || $[5] !== props.initialPlatform || $[6] !== props.open) {
    t1 = () => {
      if (!props.open) {
        initialAppliedRef.current = false;
        return;
      }
      if (initialAppliedRef.current) {
        return;
      }
      initialAppliedRef.current = true;
      if (props.initialPlatform !== undefined) {
        useGiftFormStore.getState().setPlatform(props.initialPlatform);
      }
      if (props.initialHandle !== undefined) {
        useGiftFormStore.getState().setHandle(props.initialHandle);
      }
      const currentSenderId = useGiftFormStore.getState().senderWalletId;
      if (currentSenderId === "") {
        const fallback = activeWallet?.id ?? availableWallets[0]?.id ?? "";
        if (fallback !== "") {
          useGiftFormStore.getState().setSenderWalletId(fallback);
        }
      }
      onEvent("gift_modal_opened", {});
    };
    $[2] = activeWallet?.id;
    $[3] = availableWallets;
    $[4] = props.initialHandle;
    $[5] = props.initialPlatform;
    $[6] = props.open;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== props.initialHandle || $[9] !== props.initialPlatform || $[10] !== props.open) {
    t2 = [props.open, props.initialPlatform, props.initialHandle];
    $[8] = props.initialHandle;
    $[9] = props.initialPlatform;
    $[10] = props.open;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[12] !== multiWalletEnabled || $[13] !== primaryWallet || $[14] !== props.open) {
    t3 = () => {
      if (!props.open || multiWalletEnabled || primaryWallet === null) {
        return;
      }
      if (useGiftFormStore.getState().senderWalletId === primaryWallet.id) {
        return;
      }
      useGiftFormStore.getState().setSenderWalletId(primaryWallet.id);
    };
    t4 = [props.open, multiWalletEnabled, primaryWallet];
    $[12] = multiWalletEnabled;
    $[13] = primaryWallet;
    $[14] = props.open;
    $[15] = t3;
    $[16] = t4;
  } else {
    t3 = $[15];
    t4 = $[16];
  }
  useEffect(t3, t4);
  const activeGift = asset === "usdc" ? usdcGift : outcomeGift;
  const step = activeGift.step;
  const sendError = activeGift.error;
  const usdcResult = asset === "usdc" ? usdcGift.result : null;
  const outcomeResult = asset === "outcome_token" ? outcomeGift.result : null;
  const result = usdcResult ?? outcomeResult;
  const senderWallet = resolveGiftSenderWallet({
    wallets: availableWallets,
    senderWalletId,
    activeWallet,
    multiWalletEnabled,
    primaryWallet
  });
  const senderAddress = senderWallet?.address ?? "";
  const {
    perWallet,
    perWalletRaw
  } = useWalletBalances(senderAddress.length > 0 ? [senderAddress] : []);
  const balanceUsdc = perWallet.get(senderAddress) ?? 0;
  const balanceD6Bigint = perWalletRaw.get(senderAddress) ?? BigInt(0);
  const balanceD6BN = new BN(balanceD6Bigint.toString());
  const morfi = useHandleCheck(platform === "morfi" ? handle : "");
  const x = useXHandleCheck(platform === "x" ? handle : "");
  const handleResolved = platform === "morfi" ? morfi.status === "found" : x.status === "found";
  let t5;
  if ($[17] !== handle || $[18] !== platform || $[19] !== x) {
    t5 = platform === "morfi" ? `@${handle.toLowerCase()}` : `@${(x.canonicalScreenName ?? handle).replace(/^@/, "")}`;
    $[17] = handle;
    $[18] = platform;
    $[19] = x;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const recipientDisplay = t5;
  const amountNum = Number.parseFloat(amount);
  const usdcAmountD6 = maxOverrideD6Str !== null ? new BN(maxOverrideD6Str) : Number.isFinite(amountNum) && amountNum > 0 ? new BN(Math.floor(toUsdc(amountNum) * D6_SCALE)) : null;
  const usdcAmountTooLow = usdcAmountD6?.lt(MIN_GIFT_AMOUNT_USDC_D6) === true;
  const usdcFeeD6 = usdcAmountD6 !== null ? computeGiftFeeD6(usdcAmountD6, feeBps) : new BN(0);
  const usdcTotalD6 = usdcAmountD6 !== null ? computeTotalDeductedD6(usdcAmountD6, feeBps) : new BN(0);
  const usdcExceedsBalance = usdcAmountD6 !== null && usdcTotalD6.gt(balanceD6BN);
  const usdcAmountValid = usdcAmountD6?.gt(new BN(0)) === true && !usdcAmountTooLow && !usdcExceedsBalance;
  const tokenBalanceD6 = selectedHolding !== null ? new BN(selectedHolding.balanceD6) : new BN(0);
  const tokenAmountD6 = selectedHolding === null ? null : maxOverrideD6Str !== null ? new BN(maxOverrideD6Str) : (() => {
    const parsed = parseOutcomeTokenDisplayAmountD6(amount);
    return parsed === null ? null : new BN(parsed.toString());
  })();
  const tokenSpotValueD6 = tokenAmountD6 === null || selectedHolding === null ? null : estimateOutcomeTokenSpotValueFromStringsD6(tokenAmountD6.toString(), selectedHolding.currentPriceD9);
  const tokenValueD6 = new BN(tokenSpotValueD6?.toString() ?? "0");
  const tokenExceedsBalance = tokenAmountD6?.gt(tokenBalanceD6) === true;
  const tokenAmountValid = tokenAmountD6?.gt(new BN(0)) === true && !tokenExceedsBalance;
  const recipientHandleNormalized = platform === "morfi" ? handle.trim().toLowerCase() : handle.trim().replace(/^@/, "");
  const quoteAsset = asset === "usdc" && usdcAmountD6 !== null && usdcAmountD6.gt(new BN(0)) ? {
    kind: "usdc",
    amountBaseUnits: usdcAmountD6.toString()
  } : null;
  const giftQuote = useGiftQuote({
    asset: quoteAsset,
    recipientPlatform: platform,
    recipientHandle: handleResolved ? recipientHandleNormalized : ""
  });
  const freshUsdcQuote = asset === "usdc" && giftQuote.status === "success" ? giftQuote.data : null;
  const displayAmountD6 = freshUsdcQuote !== null ? new BN(freshUsdcQuote.netToVaultBaseUnits) : asset === "usdc" ? usdcAmountD6 ?? new BN(0) : tokenValueD6;
  const displayFeeD6 = freshUsdcQuote !== null ? new BN(freshUsdcQuote.feeUsdcBaseUnits) : asset === "usdc" ? usdcFeeD6 : new BN(0);
  const willInitNextCycle = freshUsdcQuote?.willInitNextCycle === true;
  const quoteLoading = asset === "usdc" && usdcAmountD6 !== null && usdcAmountD6.gt(new BN(0)) && giftQuote.status !== "success";
  const usdcMaxGiftableD6 = computeMaxGiftAmountD6(balanceD6BN, feeBps);
  const usdcMaxGiftableUsdc = Number(usdcMaxGiftableD6.toString()) / D6_SCALE;
  const usdcMaxGiftableLocal = usdcMaxGiftableUsdc * exchangeRate;
  let t6;
  if ($[21] !== usdcMaxGiftableLocal) {
    t6 = {
      maxLocal: usdcMaxGiftableLocal
    };
    $[21] = usdcMaxGiftableLocal;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const usdcQuickAmounts = useQuickAmounts(t6);
  const tokenBalanceDisplay = selectedHolding !== null ? Number(selectedHolding.balanceD6) / D6_SCALE : 0;
  let t7;
  if ($[23] !== tokenBalanceDisplay) {
    t7 = buildTokenPercentPresets(tokenBalanceDisplay);
    $[23] = tokenBalanceDisplay;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const tokenQuickAmounts = t7;
  const messageBytes = utf8Bytes(message);
  const messageTooLong = messageBytes > GIFT_MESSAGE_MAX_BYTES;
  const submitDisabledUsdc = !handleResolved || !usdcAmountValid || messageTooLong || senderWallet === undefined || false as boolean || step === "submitting";
  const submitDisabledOutcome = !handleResolved || !tokenAmountValid || messageTooLong || senderWallet === undefined || false as boolean || step === "submitting";
  const submitDisabled = asset === "usdc" ? submitDisabledUsdc : submitDisabledOutcome;
  const nextDisabled = false as boolean || step === "submitting" || formStep === 1 && !handleResolved || formStep === 2 && (asset === "usdc" && !usdcAmountValid || asset === "outcome_token" && !tokenAmountValid) || formStep === 3 && messageTooLong;
  let t8;
  if ($[25] !== asset || $[26] !== formStep || $[27] !== handleResolved || $[28] !== markStepAttempted || $[29] !== messageTooLong || $[30] !== setStep || $[31] !== tokenAmountValid || $[32] !== usdcAmountValid) {
    t8 = function handleNext() {
      if (formStep >= TOTAL_STEPS) {
        return;
      }
      markStepAttempted(formStep);
      const state = useGiftFormStore.getState();
      if (formStep === 1) {
        const errors = getStep1Errors(state);
        if (Object.keys(errors).length > 0) {
          return;
        }
        if (!handleResolved) {
          return;
        }
      } else {
        if (formStep === 2) {
          const errors_0 = getStep2Errors(state);
          if (Object.keys(errors_0).length > 0) {
            return;
          }
          if (asset === "usdc" && !usdcAmountValid) {
            return;
          }
          if (asset === "outcome_token" && !tokenAmountValid) {
            return;
          }
        } else {
          if (formStep === 3) {
            if (messageTooLong) {
              return;
            }
          }
        }
      }
      setStep(formStep + 1 as 1 | 2 | 3 | 4);
    };
    $[25] = asset;
    $[26] = formStep;
    $[27] = handleResolved;
    $[28] = markStepAttempted;
    $[29] = messageTooLong;
    $[30] = setStep;
    $[31] = tokenAmountValid;
    $[32] = usdcAmountValid;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  const handleNext = t8;
  let t9;
  if ($[34] !== formStep || $[35] !== setStep) {
    t9 = function handleBack() {
      if (formStep > 1) {
        setStep(formStep - 1 as 1 | 2 | 3 | 4);
      }
    };
    $[34] = formStep;
    $[35] = setStep;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  const handleBack = t9;
  let t10;
  if ($[37] !== setStep) {
    t10 = function handleSkipMessage() {
      useGiftFormStore.getState().setMessage("");
      useGiftFormStore.getState().setAnonymous(false);
      setStep(4);
    };
    $[37] = setStep;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  const handleSkipMessage = t10;
  let t11;
  if ($[39] !== phase || $[40] !== props || $[41] !== reset || $[42] !== step) {
    t11 = function handleClose() {
      if (false as boolean || step === "submitting") {
        return;
      }
      if (phase === "compose") {
        onEvent("gift_modal_dismissed", {});
      }
      reset();
      setPhase("compose");
      props.onOpenChange(false);
    };
    $[39] = phase;
    $[40] = props;
    $[41] = reset;
    $[42] = step;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  const handleClose = t11;
  let t12;
  if ($[44] !== handleClose || $[45] !== props) {
    t12 = function handleOpenChange(next) {
      if (!next) {
        handleClose();
        return;
      }
      props.onOpenChange(true);
    };
    $[44] = handleClose;
    $[45] = props;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  const handleOpenChange = t12;
  const handleSubmit = async function handleSubmit() {
    if (submitDisabled || senderWallet === undefined) {
      giftLog("modal: handleSubmit blocked", {
        submitDisabled,
        hasSenderWallet: senderWallet !== undefined
      });
      return;
    }
    if (senderWallet.id !== primaryWallet?.id && !canStartOperation("switch", "gift_send")) {
      return;
    }
    onEvent("gift_send_initiated", {});
    giftLog("modal: handleSubmit dispatching", {
      asset,
      platform,
      anonymous,
      senderWalletId: senderWallet.id,
      senderWalletType: senderWallet.type,
      recipientHandleNormalized,
      messageBytes: new TextEncoder().encode(message).byteLength
    });
    if (asset === "usdc") {
      if (usdcAmountD6 === null) {
        giftLog("modal: usdc submit aborted \u2014 usdcAmountD6 null");
        return;
      }
      const sent = await usdcGift.submit({
        platform,
        recipientHandle: recipientHandleNormalized,
        asset: {
          kind: "usdc",
          amountBaseUnits: usdcAmountD6.toString()
        },
        message,
        anonymous,
        senderWalletId: senderWallet.id
      });
      if (sent !== null) {
        onEvent("gift_send_completed", {
          depositPubkey: sent.giftPda
        });
        setPhase("success");
      }
      return;
    }
    if (tokenAmountD6 === null || selectedHolding === null) {
      giftLog("modal: outcome-token submit aborted", {
        hasTokenAmountD6: tokenAmountD6 !== null,
        hasSelectedHolding: selectedHolding !== null
      });
      return;
    }
    const outcomeMint = deriveOutcomeMintPda(new PublicKey(selectedHolding.marketPubkey), selectedHolding.outcomeIndex, networkProgramId())[0].toBase58();
    const sent_0 = await outcomeGift.submit({
      platform,
      recipientHandle: recipientHandleNormalized,
      senderWalletId: senderWallet.id,
      asset: {
        kind: "outcome_token",
        mintPubkey: outcomeMint,
        amountBaseUnits: tokenAmountD6.toString()
      },
      message,
      anonymous
    });
    if (sent_0 !== null) {
      onEvent("gift_send_completed", {
        depositPubkey: sent_0.giftPda
      });
      setPhase("success");
    }
  };
  let t13;
  let t14;
  if ($[47] !== result || $[48] !== step) {
    t13 = () => {
      if (step === "confirmed" && result !== null) {
        setPhase("success");
      }
    };
    t14 = [step, result];
    $[47] = result;
    $[48] = step;
    $[49] = t13;
    $[50] = t14;
  } else {
    t13 = $[49];
    t14 = $[50];
  }
  useEffect(t13, t14);
  const successAmountD6 = asset === "usdc" ? usdcAmountD6?.toString() ?? "0" : tokenValueD6.toString();
  const permalinkUrl = result !== null ? result.permalinkUrl : "";
  const isOutcomeSend = asset === "outcome_token";
  let t15;
  if ($[51] !== isOutcomeSend || $[52] !== selectedHolding) {
    t15 = isOutcomeSend && selectedHolding !== null ? deriveOutcomeMintPda(new PublicKey(selectedHolding.marketPubkey), selectedHolding.outcomeIndex, networkProgramId())[0].toBase58() : null;
    $[51] = isOutcomeSend;
    $[52] = selectedHolding;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  const outcomeSendMint = t15;
  const outcomeSendAmountD6 = isOutcomeSend && tokenAmountD6 !== null ? tokenAmountD6.toString() : "0";
  const outcomeSendLabel = isOutcomeSend && selectedHolding !== null ? selectedHolding.outcomeLabel : null;
  const outcomeSendMarketTitle = isOutcomeSend && selectedHolding !== null ? selectedHolding.marketTitle : null;
  let t16;
  if ($[54] !== reset) {
    t16 = function onSendAnother() {
      setPhase("compose");
      reset();
    };
    $[54] = reset;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  const onSendAnother = t16;
  let t17;
  if ($[56] !== asset || $[57] !== balanceD6Bigint || $[58] !== balanceUsdc || $[59] !== displayAmountD6 || $[60] !== displayFeeD6 || $[61] !== formStep || $[62] !== handleBack || $[63] !== handleNext || $[64] !== handleOpenChange || $[65] !== handleSkipMessage || $[66] !== handleSubmit || $[67] !== morfi || $[68] !== nextDisabled || $[69] !== onSendAnother || $[70] !== outcomeSendAmountD6 || $[71] !== outcomeSendLabel || $[72] !== outcomeSendMarketTitle || $[73] !== outcomeSendMint || $[74] !== permalinkUrl || $[75] !== phase || $[76] !== platform || $[77] !== quoteLoading || $[78] !== recipientDisplay || $[79] !== recipientHandleNormalized || $[80] !== result || $[81] !== sendError || $[82] !== senderAddress || $[83] !== step || $[84] !== submitDisabled || $[85] !== successAmountD6 || $[86] !== tokenQuickAmounts || $[87] !== usdcQuickAmounts || $[88] !== willInitNextCycle || $[89] !== x) {
    t17 = {
      formStep,
      asset,
      platform,
      step,
      sendError,
      errorKey: null,
      phase,
      result,
      morfi,
      x,
      balanceUsdc,
      balanceD6Bigint,
      senderAddress,
      usdcQuickAmounts,
      tokenQuickAmounts,
      displayAmountD6,
      displayFeeD6,
      willInitNextCycle,
      quoteLoading,
      nextDisabled,
      submitDisabled,
      recipientDisplay,
      recipientHandleNormalized,
      successAmountD6,
      permalinkUrl,
      outcomeSendMint,
      outcomeSendAmountD6,
      outcomeSendLabel,
      outcomeSendMarketTitle,
      handleNext,
      handleBack,
      handleSkipMessage,
      handleSubmit,
      handleOpenChange,
      onSendAnother
    };
    $[56] = asset;
    $[57] = balanceD6Bigint;
    $[58] = balanceUsdc;
    $[59] = displayAmountD6;
    $[60] = displayFeeD6;
    $[61] = formStep;
    $[62] = handleBack;
    $[63] = handleNext;
    $[64] = handleOpenChange;
    $[65] = handleSkipMessage;
    $[66] = handleSubmit;
    $[67] = morfi;
    $[68] = nextDisabled;
    $[69] = onSendAnother;
    $[70] = outcomeSendAmountD6;
    $[71] = outcomeSendLabel;
    $[72] = outcomeSendMarketTitle;
    $[73] = outcomeSendMint;
    $[74] = permalinkUrl;
    $[75] = phase;
    $[76] = platform;
    $[77] = quoteLoading;
    $[78] = recipientDisplay;
    $[79] = recipientHandleNormalized;
    $[80] = result;
    $[81] = sendError;
    $[82] = senderAddress;
    $[83] = step;
    $[84] = submitDisabled;
    $[85] = successAmountD6;
    $[86] = tokenQuickAmounts;
    $[87] = usdcQuickAmounts;
    $[88] = willInitNextCycle;
    $[89] = x;
    $[90] = t17;
  } else {
    t17 = $[90];
  }
  return t17;
}

/**
 * Multi-step send-gift flow (orchestrator).
 *
 * Modeled on `create-proposal-dialog.tsx`. Three stages: (1) recipient,
 * (2) gift composition, (3) review & send. State lives in `useGiftFormStore`;
 * step components are dumb readers/writers of that store. The cross-cutting
 * concerns (live wallet balance, server-authoritative quote, navigation,
 * on-chain submission, success hand-off) live in `useSendGiftModal`.
 *
 * The cash review uses the server's fee-on-top quote. The live outcome-token
 * transaction transfers only the selected tokens; its D6 spot value is an
 * indicative display and no USDC outcome-gift fee is shown as charged.
 */
function _temp14(s_11) {
  return s_11.markStepAttempted;
}
function _temp13(s_10) {
  return s_10.reset;
}
function _temp12(s_9) {
  return s_9.setStep;
}
function _temp11(s_8) {
  return s_8.maxOverrideD6Str;
}
function _temp10(s_7) {
  return s_7.anonymous;
}
function _temp9(s_6) {
  return s_6.message;
}
function _temp8(s_5) {
  return s_5.selectedHolding;
}
function _temp7(s_4) {
  return s_4.amount;
}
function _temp6(s_3) {
  return s_3.asset;
}
function _temp5(s_2) {
  return s_2.senderWalletId;
}
function _temp4(s_1) {
  return s_1.handle;
}
function _temp3(s_0) {
  return s_0.platform;
}
function _temp2(s) {
  return s.step;
}
function _temp(wallet) {
  return wallet.isAvailable !== false;
}
export function SendGiftModal(props) {
  const $ = _c(127);
  const t = useTranslations("gift.send");
  const tSteps = useTranslations("gift.send.steps");
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const c = useSendGiftModal(props);
  if (c.phase === "success" && c.result !== null) {
    let t0;
    if ($[0] !== c.asset || $[1] !== c.handleOpenChange || $[2] !== c.onSendAnother || $[3] !== c.outcomeSendAmountD6 || $[4] !== c.outcomeSendLabel || $[5] !== c.outcomeSendMarketTitle || $[6] !== c.outcomeSendMint || $[7] !== c.permalinkUrl || $[8] !== c.platform || $[9] !== c.recipientDisplay || $[10] !== c.recipientHandleNormalized || $[11] !== c.result.giftPda || $[12] !== c.successAmountD6 || $[13] !== props.open) {
      t0 = <GiftSuccessModal open={props.open} onOpenChange={c.handleOpenChange} permalinkUrl={c.permalinkUrl} recipientDisplay={c.recipientDisplay} recipientPlatform={c.platform} recipientHandle={c.recipientHandleNormalized} amountD6={c.successAmountD6} assetKind={c.asset} outcomeMintPubkey={c.outcomeSendMint} outcomeAmountBaseUnits={c.outcomeSendAmountD6} outcomeLabelFallback={c.outcomeSendLabel} marketTitleFallback={c.outcomeSendMarketTitle} depositPubkey={c.result.giftPda} onSendAnother={c.onSendAnother} />;
      $[0] = c.asset;
      $[1] = c.handleOpenChange;
      $[2] = c.onSendAnother;
      $[3] = c.outcomeSendAmountD6;
      $[4] = c.outcomeSendLabel;
      $[5] = c.outcomeSendMarketTitle;
      $[6] = c.outcomeSendMint;
      $[7] = c.permalinkUrl;
      $[8] = c.platform;
      $[9] = c.recipientDisplay;
      $[10] = c.recipientHandleNormalized;
      $[11] = c.result.giftPda;
      $[12] = c.successAmountD6;
      $[13] = props.open;
      $[14] = t0;
    } else {
      t0 = $[14];
    }
    return t0;
  }
  let t0;
  if ($[15] !== tSteps) {
    t0 = tSteps("step1Title");
    $[15] = tSteps;
    $[16] = t0;
  } else {
    t0 = $[16];
  }
  let t1;
  if ($[17] !== tSteps) {
    t1 = tSteps("step2Title");
    $[17] = tSteps;
    $[18] = t1;
  } else {
    t1 = $[18];
  }
  let t2;
  if ($[19] !== tSteps) {
    t2 = tSteps("step3Title");
    $[19] = tSteps;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  let t3;
  if ($[21] !== tSteps) {
    t3 = tSteps("step4Title");
    $[21] = tSteps;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  let t4;
  if ($[23] !== t0 || $[24] !== t1 || $[25] !== t2 || $[26] !== t3) {
    t4 = {
      1: t0,
      2: t1,
      3: t2,
      4: t3
    };
    $[23] = t0;
    $[24] = t1;
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  const stepTitles = t4;
  const t5 = props.open;
  const t6 = c.handleOpenChange;
  let t7;
  if ($[28] !== c.formStep || $[29] !== stepTitles || $[30] !== t) {
    t7 = stepTitles[c.formStep] ?? t("title");
    $[28] = c.formStep;
    $[29] = stepTitles;
    $[30] = t;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== t) {
    t8 = t("description");
    $[32] = t;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== c.formStep || $[35] !== stepTitles || $[36] !== t) {
    t9 = stepTitles[c.formStep] ?? t("title");
    $[34] = c.formStep;
    $[35] = stepTitles;
    $[36] = t;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setHowItWorksOpen(true);
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== t) {
    t11 = t("howItWorks.trigger");
    $[39] = t;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Icon icon={InformationCircleIcon} className="size-4" aria-hidden="true" />;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== t11) {
    t13 = <button type="button" onClick={t10} aria-label={t11} className="text-foreground-secondary transition-colors hover:text-foreground-primary focus-visible:text-foreground-primary focus-visible:outline-none">{t12}</button>;
    $[42] = t11;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== t13 || $[45] !== t9) {
    t14 = <span className="flex items-center gap-1.5 text-heading-md">{t9}{t13}</span>;
    $[44] = t13;
    $[45] = t9;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== t) {
    t15 = t("description");
    $[47] = t;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== t15) {
    t16 = <span className="text-body text-foreground-secondary">{t15}</span>;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== c.formStep) {
    t17 = <StepIndicator currentStep={c.formStep} totalSteps={TOTAL_STEPS} />;
    $[51] = c.formStep;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== t14 || $[54] !== t16 || $[55] !== t17) {
    t18 = <div className="flex flex-col gap-2">{t14}{t16}{t17}</div>;
    $[53] = t14;
    $[54] = t16;
    $[55] = t17;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  const t19 = c.handleBack;
  const t20 = c.formStep === 1 || false as boolean || c.step === "submitting";
  let t21;
  if ($[57] !== tSteps) {
    t21 = tSteps("back");
    $[57] = tSteps;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== c.handleBack || $[60] !== t20 || $[61] !== t21) {
    t22 = <AnimatedButton type="button" variant="secondary" onClick={t19} disabled={t20}>{t21}</AnimatedButton>;
    $[59] = c.handleBack;
    $[60] = t20;
    $[61] = t21;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== c.formStep || $[64] !== c.handleSkipMessage || $[65] !== c.step || $[66] !== tSteps) {
    t23 = c.formStep === 3 ? <AnimatedButton type="button" variant="tertiary" onClick={c.handleSkipMessage} disabled={false as boolean || c.step === "submitting"}>{tSteps("skip")}</AnimatedButton> : null;
    $[63] = c.formStep;
    $[64] = c.handleSkipMessage;
    $[65] = c.step;
    $[66] = tSteps;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== c || $[69] !== t || $[70] !== tSteps) {
    t24 = c.formStep < TOTAL_STEPS ? <AnimatedButton type="button" onClick={c.handleNext} disabled={c.nextDisabled}>{tSteps("next")}</AnimatedButton> : <AnimatedButton type="button" onClick={() => {
      c.handleSubmit();
    }} disabled={c.submitDisabled} loading={c.step === "submitting"}>{false as boolean || c.step === "submitting" ? t("signing") : t("submit")}</AnimatedButton>;
    $[68] = c;
    $[69] = t;
    $[70] = tSteps;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== t23 || $[73] !== t24) {
    t25 = <div className="flex items-center gap-2">{t23}{t24}</div>;
    $[72] = t23;
    $[73] = t24;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] !== t22 || $[76] !== t25) {
    t26 = <div className="flex justify-between">{t22}{t25}</div>;
    $[75] = t22;
    $[76] = t25;
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  let t27;
  if ($[78] !== c.asset || $[79] !== c.formStep || $[80] !== c.sendError) {
    t27 = [c.formStep, c.asset, c.sendError];
    $[78] = c.asset;
    $[79] = c.formStep;
    $[80] = c.sendError;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[82] !== c.formStep || $[83] !== c.morfi || $[84] !== c.x) {
    t28 = c.formStep === 1 && <SendGiftStep1 morfi={c.morfi} x={c.x} />;
    $[82] = c.formStep;
    $[83] = c.morfi;
    $[84] = c.x;
    $[85] = t28;
  } else {
    t28 = $[85];
  }
  let t29;
  if ($[86] !== c.balanceD6Bigint || $[87] !== c.balanceUsdc || $[88] !== c.formStep || $[89] !== c.senderAddress || $[90] !== c.tokenQuickAmounts || $[91] !== c.usdcQuickAmounts) {
    t29 = c.formStep === 2 && <SendGiftStep2 balanceUsdc={c.balanceUsdc} balanceD6Bigint={c.balanceD6Bigint} senderAddress={c.senderAddress} usdcQuickAmounts={c.usdcQuickAmounts} tokenQuickAmounts={c.tokenQuickAmounts} />;
    $[86] = c.balanceD6Bigint;
    $[87] = c.balanceUsdc;
    $[88] = c.formStep;
    $[89] = c.senderAddress;
    $[90] = c.tokenQuickAmounts;
    $[91] = c.usdcQuickAmounts;
    $[92] = t29;
  } else {
    t29 = $[92];
  }
  let t30;
  if ($[93] !== c.formStep) {
    t30 = c.formStep === 3 && <SendGiftStep3 />;
    $[93] = c.formStep;
    $[94] = t30;
  } else {
    t30 = $[94];
  }
  let t31;
  if ($[95] !== c.displayAmountD6 || $[96] !== c.displayFeeD6 || $[97] !== c.formStep || $[98] !== c.quoteLoading || $[99] !== c.recipientDisplay || $[100] !== c.willInitNextCycle) {
    t31 = c.formStep === 4 && <SendGiftStep4 recipientDisplay={c.recipientDisplay} amountD6={c.displayAmountD6} feeD6={c.displayFeeD6} willInitNextCycle={c.willInitNextCycle} quoteLoading={c.quoteLoading} />;
    $[95] = c.displayAmountD6;
    $[96] = c.displayFeeD6;
    $[97] = c.formStep;
    $[98] = c.quoteLoading;
    $[99] = c.recipientDisplay;
    $[100] = c.willInitNextCycle;
    $[101] = t31;
  } else {
    t31 = $[101];
  }
  let t32;
  if ($[102] !== c.errorKey || $[103] !== c.sendError || $[104] !== t) {
    t32 = c.sendError !== null ? <p className="text-caption text-feedback-error-content" role="alert">{c.errorKey === "gift.send.error.transferFeeMintNotSupported" ? t("error.transferFeeMintNotSupported") : c.errorKey === "gift.send.error.noOutcomeSelected" ? t("error.noOutcomeSelected") : t("error.generic")}</p> : null;
    $[102] = c.errorKey;
    $[103] = c.sendError;
    $[104] = t;
    $[105] = t32;
  } else {
    t32 = $[105];
  }
  let t33;
  if ($[106] !== t27 || $[107] !== t28 || $[108] !== t29 || $[109] !== t30 || $[110] !== t31 || $[111] !== t32) {
    t33 = <MotionAnimatedAutoHeight layoutDependencies={t27} innerClassName="flex flex-col gap-4">{t28}{t29}{t30}{t31}{t32}</MotionAnimatedAutoHeight>;
    $[106] = t27;
    $[107] = t28;
    $[108] = t29;
    $[109] = t30;
    $[110] = t31;
    $[111] = t32;
    $[112] = t33;
  } else {
    t33 = $[112];
  }
  let t34;
  if ($[113] !== c.handleOpenChange || $[114] !== props.open || $[115] !== t18 || $[116] !== t26 || $[117] !== t33 || $[118] !== t7 || $[119] !== t8) {
    t34 = <SilkSheet open={t5} onOpenChange={t6} variant="page" size="md" title={t7} description={t8} header={t18} footer={t26}>{t33}</SilkSheet>;
    $[113] = c.handleOpenChange;
    $[114] = props.open;
    $[115] = t18;
    $[116] = t26;
    $[117] = t33;
    $[118] = t7;
    $[119] = t8;
    $[120] = t34;
  } else {
    t34 = $[120];
  }
  const t35 = c.asset === "usdc";
  let t36;
  if ($[121] !== howItWorksOpen || $[122] !== t35) {
    t36 = <GiftHowItWorksSheet open={howItWorksOpen} onOpenChange={setHowItWorksOpen} showFee={t35} />;
    $[121] = howItWorksOpen;
    $[122] = t35;
    $[123] = t36;
  } else {
    t36 = $[123];
  }
  let t37;
  if ($[124] !== t34 || $[125] !== t36) {
    t37 = <>{t34}{t36}</>;
    $[124] = t34;
    $[125] = t36;
    $[126] = t37;
  } else {
    t37 = $[126];
  }
  return t37;
}